/*
 * Vajihi Labs - Email Signature Builder
 * Signature-generation layer, v3 (premium theme rebuild).
 *
 * Five themes — Executive, Studio, Prestige, Connect, Essential — rebuilt to
 * match references/APPROVED-PREMIUM-COLLECTION.png from the Sept 14 design
 * handoff. Per the confirmed decision on that handoff: layouts use pure
 * table/CSS (colored panels, rails, rounded badges, tinted cards, rules) —
 * no icon or decorative-motif images, since there's still no hosting for
 * those (Phase 4 isn't built) and the handoff's own rules forbid inventing
 * hosting URLs. Contact and social "icons" are small colored text badges
 * instead of brand-icon graphics, same philosophy as the previous build's
 * text-only social links.
 *
 * Table-based layout, inline styles only — no <style> blocks, no flex/grid,
 * no SVG, no external stylesheets or web fonts inside the signature itself.
 */

import { escapeAttr, escapeHtml, sanitizeLogoUrl, sanitizeUrl } from "./validation";

export type ThemeId = "basic" | "premium-executive-v2" | "premium-studio-v2" | "premium-prestige-v2" | "premium-connect-v2" | "premium-essential-v2";

/** Legacy theme ids from the pre-Sept-14 build, kept only so stored data can
 *  be migrated — never offered as a choice again. */
export type LegacyThemeId = "executive" | "minimal" | "corporate" | "modern" | "compact" | "bilingual";

/** Per integration/migration.json's legacySuggestionMap. `null` means no
 *  equivalent new design (Bilingual) — the stored Arabic content is kept,
 *  never discarded, but the person must pick one of the 5 new themes. */
export const LEGACY_THEME_MIGRATION: Record<LegacyThemeId, ThemeId | null> = {
  executive: "premium-executive-v2",
  minimal: "premium-essential-v2",
  corporate: "premium-executive-v2",
  modern: "premium-connect-v2",
  compact: "premium-essential-v2",
  bilingual: null,
};

export const LEGACY_THEME_IDS: LegacyThemeId[] = ["executive", "minimal", "corporate", "modern", "compact", "bilingual"];

export type SocialType = "linkedin" | "instagram" | "facebook" | "x" | "website";

export interface SocialEntry {
  id: SocialType;
  enabled: boolean;
  url: string;
}

export interface PersonFields {
  name: string;
  title: string;
  phone: string;
  email: string;
}

export interface CompanyFields {
  name: string;
  website: string;
  websiteLabel: string;
}

export interface LogoFields {
  /** Hosted URL for use on light/white backgrounds (Studio, Prestige,
   *  Essential's right lockup). This — not a local preview — is what
   *  actually resolves for a recipient. */
  lightSurfaceUrl: string;
  /** Hosted URL for use on dark/colored backgrounds (Executive's navy
   *  panel, Connect's round badge). Optional — falls back to
   *  lightSurfaceUrl, which may not look right against a colored panel;
   *  that's a documented, honest degradation, not a bug. */
  darkSurfaceUrl: string;
  alt: string;
}

export interface BrandingFields {
  useThemePalette: boolean;
  accentOverride: string | null;
  tagline: string;
  footerStatement: string;
  sideStatement: string;
}

/** Legacy-only: carried over from a pre-Sept-14 Bilingual signature so it's
 *  never silently discarded. Not editable anywhere in the v3 UI. */
export interface ArabicFields {
  name: string;
  title: string;
  companyName: string;
}

export interface SignatureModel {
  schemaVersion: 2;
  themeId: ThemeId;
  person: PersonFields;
  company: CompanyFields;
  logos: LogoFields;
  branding: BrandingFields;
  socials: SocialEntry[];
  disclaimer: string;
  /** Present only if migrated from a legacy Bilingual signature. */
  arabic: ArabicFields;
}

export interface ThemeMeta {
  id: ThemeId;
  name: string;
  caption: string;
  description: string;
  defaultPalette: { accent: string; ink: string; surface: string };
}

/** Not part of THEMES below — Basic isn't "browsed" in the themed grid, it's
 *  the other half of the Templates entry toggle (Basic vs Browse Themes).
 *  Kept in the same ThemeMeta shape purely so the Basic panel can reuse the
 *  same name/caption rendering as a themed card. */
export const BASIC_THEME: ThemeMeta = {
  id: "basic",
  name: "Basic",
  caption: "Simple & clean",
  description: "Logo, name, title and company on a plain white background — no color, no icons, no theme styling.",
  defaultPalette: { accent: "#667085", ink: "#12172B", surface: "#FFFFFF" },
};

export const THEMES: ThemeMeta[] = [
  {
    id: "premium-executive-v2",
    name: "Executive",
    caption: "Confident & corporate",
    description: "Navy brand panel, bold identity, two-column contacts, indigo footer rule.",
    defaultPalette: { accent: "#1E46D9", ink: "#082044", surface: "#FFFFFF" },
  },
  {
    id: "premium-studio-v2",
    name: "Studio",
    caption: "Fresh & expressive",
    description: "Emerald editorial identity, serif name, divided contacts and mint decorative edge.",
    defaultPalette: { accent: "#0B5738", ink: "#103426", surface: "#FFFFFF" },
  },
  {
    id: "premium-prestige-v2",
    name: "Prestige",
    caption: "Refined & elegant",
    description: "Ivory and bronze stationery with a prominent serif name and right-aligned logo.",
    defaultPalette: { accent: "#906A49", ink: "#232427", surface: "#FCF9F4" },
  },
  {
    id: "premium-connect-v2",
    name: "Connect",
    caption: "Bold & approachable",
    description: "Round brand badge, strong name and tinted contact card with a bottom social strip.",
    defaultPalette: { accent: "#2455EF", ink: "#082044", surface: "#FFFFFF" },
  },
  {
    id: "premium-essential-v2",
    name: "Essential",
    caption: "Clean & focused",
    description: "Bold cobalt rail, concise identity and separated contact strip.",
    defaultPalette: { accent: "#2355EE", ink: "#082044", surface: "#FFFFFF" },
  },
];

export const SOCIAL_TYPES: SocialType[] = ["linkedin", "instagram", "facebook", "x", "website"];

export const SOCIAL_LABELS: Record<SocialType, string> = {
  linkedin: "LinkedIn",
  instagram: "Instagram",
  facebook: "Facebook",
  x: "X",
  website: "Website",
};

/** A brand-new install's toggles: linkedin/instagram/website on with the
 *  same reserved example.com destinations the design handoff's own sample
 *  data uses (demonstration only — never real profiles), so the shipped
 *  default is genuinely "Ready" like the approved reference shows, not
 *  blocked on empty URLs the person hasn't touched yet. */
export function defaultSocials(): SocialEntry[] {
  return [
    { id: "linkedin", enabled: true, url: "https://example.com/linkedin" },
    { id: "instagram", enabled: true, url: "https://example.com/instagram" },
    { id: "facebook", enabled: false, url: "" },
    { id: "x", enabled: false, url: "" },
    { id: "website", enabled: true, url: "https://example.com" },
  ];
}

/** Seed data a brand-new install ships with, so the live preview always
 *  shows a coherent signature immediately (matches the approved reference's
 *  own "Jordan Smith / Vajihi Trading Co." sample). `isSampleData()` below
 *  detects whether a model still looks untouched. */
export function defaultModel(): SignatureModel {
  return {
    schemaVersion: 2,
    themeId: "basic",
    person: {
      name: "Jordan Smith",
      title: "Business Development Manager",
      phone: "+973 1700 0000",
      email: "jordan@example.com",
    },
    company: {
      name: "Vajihi Trading Co.",
      website: "https://example.com",
      websiteLabel: "www.example.com",
    },
    logos: { lightSurfaceUrl: "", darkSurfaceUrl: "", alt: "Company logo" },
    branding: { useThemePalette: true, accentOverride: null, tagline: "", footerStatement: "", sideStatement: "" },
    socials: defaultSocials(),
    disclaimer: "",
    arabic: { name: "", title: "", companyName: "" },
  };
}

/** True if the model still matches the shipped seed data exactly for the
 *  fields that most obviously mark it as a sample (email is the least
 *  likely field a real user would leave untouched). Used only to label
 *  Templates previews "Sample" — never used to gate anything real. */
export function isSampleData(model: SignatureModel): boolean {
  return model.person.email === "jordan@example.com" && model.person.name === "Jordan Smith";
}

const FONT_SANS = "Arial, Helvetica, sans-serif";
const FONT_SERIF = "Georgia, 'Times New Roman', serif";

function cell(value: string, style = ""): string {
  return `<td style="padding:0;vertical-align:top;${style}">${value}</td>`;
}

function table(rows: string, style = ""): string {
  return `<table role="presentation" border="0" cellpadding="0" cellspacing="0" style="border-collapse:collapse;${style}">${rows}</table>`;
}

function line(value: string, style = ""): string {
  return value ? `<div style="margin:0;${style}">${value}</div>` : "";
}

function link(url: string, label: string, color: string, style = ""): string {
  return `<a href="${escapeAttr(url)}" style="color:${color};text-decoration:none;${style}">${label}</a>`;
}

/** A small colored circular badge carrying a 1-2 character text label —
 *  stands in for a contact/social icon image without depending on any
 *  hosted asset. Degrades to a colored square in clients that ignore
 *  border-radius on inline elements (documented Outlook-desktop quirk),
 *  which still reads fine since the label text is always present. */
function badge(text: string, bg: string, fg = "#FFFFFF", size = 18): string {
  return `<span style="display:inline-block;width:${size}px;height:${size}px;line-height:${size}px;border-radius:50%;background-color:${bg};color:${fg};text-align:center;font-family:${FONT_SANS};font-size:${Math.max(8, Math.round(size * 0.42))}px;font-weight:bold;">${text}</span>`;
}

function isValidHex(value: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(value);
}

function resolveAccent(model: SignatureModel, themeDefaultAccent: string): string {
  if (!model.branding.useThemePalette && model.branding.accentOverride && isValidHex(model.branding.accentOverride)) {
    return model.branding.accentOverride;
  }
  return themeDefaultAccent;
}

function resolveLogo(model: SignatureModel, surface: "light" | "dark"): string {
  const primary = surface === "dark" ? model.logos.darkSurfaceUrl : model.logos.lightSurfaceUrl;
  const fallback = surface === "dark" ? model.logos.lightSurfaceUrl : "";
  return sanitizeLogoUrl(primary) || sanitizeLogoUrl(fallback) || "";
}

function logoImgTag(model: SignatureModel, surface: "light" | "dark", widthPx: number, heightPx: number, overrideUrl?: string): string {
  const url = overrideUrl ? sanitizeLogoUrl(overrideUrl) || "" : resolveLogo(model, surface);
  if (!url) return "";
  const alt = escapeAttr(model.logos.alt || model.company.name || "Company logo");
  return `<img src="${escapeAttr(url)}" alt="${alt}" width="${widthPx}" height="${heightPx}" style="display:block;border:0;width:${widthPx}px;height:${heightPx}px;object-fit:contain;" />`;
}

function companyMonogram(model: SignatureModel): string {
  const trimmed = model.company.name.trim();
  return trimmed ? escapeHtml(trimmed.charAt(0).toUpperCase()) : "V";
}

function websiteLink(model: SignatureModel, accent: string, style = ""): string {
  const url = sanitizeUrl(model.company.website);
  if (!url) return "";
  const label = model.company.websiteLabel.trim() || model.company.website.trim().replace(/^https?:\/\//, "").replace(/\/$/, "");
  return link(url, escapeHtml(label), accent, style);
}

function socialBadgeRow(model: SignatureModel, badgeBg: string, size = 18, gap = 6): string {
  const active = model.socials.filter((s) => s.enabled && sanitizeUrl(s.url));
  if (active.length === 0) return "";
  return active
    .map((s) => {
      const url = sanitizeUrl(s.url);
      const text = SOCIAL_LABELS[s.id].slice(0, 2);
      return `<a href="${escapeAttr(url)}" style="text-decoration:none;margin-right:${gap}px;">${badge(text, badgeBg, "#FFFFFF", size)}</a>`;
    })
    .join("");
}

/** Blocking issues that must be resolved before Insert/Copy/Download — an
 *  actionable list, never a silent throw. Matches STATES-AND-BEHAVIOR.md:
 *  "Enabled social without a valid URL creates an actionable error and
 *  must not export as a dead icon." */
export interface ValidationIssue {
  field: string;
  message: string;
}

export function validateForApply(model: SignatureModel): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!model.person.name.trim()) {
    issues.push({ field: "person.name", message: "Add your name before this can be sent." });
  }
  // Basic never renders socials, so an enabled-but-unfilled social left over
  // from a previous theme shouldn't block Insert/Copy/Download here.
  if (model.themeId !== "basic") {
    model.socials.forEach((s) => {
      if (s.enabled && !sanitizeUrl(s.url)) {
        issues.push({ field: `social.${s.id}`, message: `Add a link for ${SOCIAL_LABELS[s.id]}, or turn it off.` });
      }
    });
  }
  return issues;
}

/** True only when everything the sent signature depends on will actually
 *  resolve for a recipient — never true just because a local preview
 *  exists. Used for the honest "Logo & links: Ready" vs "Needs setup" row. */
export function isLogoAndLinksReady(model: SignatureModel, hasLocalLogoPreview: boolean): boolean {
  const hasAnyLogoIntent = hasLocalLogoPreview || !!model.logos.lightSurfaceUrl || !!model.logos.darkSurfaceUrl;
  const logoReady = !hasAnyLogoIntent || !!sanitizeLogoUrl(model.logos.lightSurfaceUrl);
  // Basic never renders socials, so their readiness shouldn't gate this row.
  const socialsReady = model.themeId === "basic" || model.socials.every((s) => !s.enabled || !!sanitizeUrl(s.url));
  return logoReady && socialsReady;
}

function contactStack(model: SignatureModel, accent: string, badgeBg: string, badgeSize: number): string {
  const rows: string[] = [];
  if (model.person.phone.trim()) {
    const tel = model.person.phone.replace(/[^+0-9]/g, "");
    const text = tel ? link(`tel:${tel}`, escapeHtml(model.person.phone), accent) : escapeHtml(model.person.phone);
    rows.push(`${badge("T", badgeBg, "#FFFFFF", badgeSize)}&nbsp;&nbsp;${text}`);
  }
  if (model.person.email.trim() && /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(model.person.email)) {
    rows.push(`${badge("E", badgeBg, "#FFFFFF", badgeSize)}&nbsp;&nbsp;${link(`mailto:${encodeURIComponent(model.person.email)}`, escapeHtml(model.person.email), accent)}`);
  }
  const website = websiteLink(model, accent);
  if (website) {
    rows.push(`${badge("W", badgeBg, "#FFFFFF", badgeSize)}&nbsp;&nbsp;${website}`);
  }
  return rows.join("");
}

// ---------------------------------------------------------------------------
// 01 / Executive — navy brand panel, two-column contacts, indigo footer rule.
// ---------------------------------------------------------------------------
function renderExecutive(model: SignatureModel): string {
  const meta = THEMES.find((t) => t.id === "premium-executive-v2")!;
  const accent = resolveAccent(model, meta.defaultPalette.accent);
  const ink = meta.defaultPalette.ink;
  const name = escapeHtml(model.person.name);
  const title = escapeHtml(model.person.title);
  const company = escapeHtml(model.company.name);
  const logo = logoImgTag(model, "dark", 72, 72);

  const panelInner =
    (logo || `<span style="display:inline-block;width:72px;height:72px;line-height:72px;text-align:center;color:#FFFFFF;font-family:${FONT_SANS};font-size:30px;font-weight:bold;">${companyMonogram(model)}</span>`) +
    line(company, `color:#FFFFFF;font-weight:bold;font-size:14px;margin-top:10px;`) +
    (model.branding.tagline.trim()
      ? line(escapeHtml(model.branding.tagline.trim().toUpperCase()), "color:#AEB8D6;font-size:9px;letter-spacing:1.5px;margin-top:8px;")
      : "");

  const phoneRow = model.person.phone.trim()
    ? line(`${badge("T", accent)}&nbsp;&nbsp;${(() => {
        const tel = model.person.phone.replace(/[^+0-9]/g, "");
        return tel ? link(`tel:${tel}`, escapeHtml(model.person.phone), ink) : escapeHtml(model.person.phone);
      })()}`, "margin-bottom:6px;")
    : "";
  const emailRow =
    model.person.email.trim() && /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(model.person.email)
      ? line(`${badge("E", accent)}&nbsp;&nbsp;${link(`mailto:${encodeURIComponent(model.person.email)}`, escapeHtml(model.person.email), ink)}`)
      : "";
  const websiteRow = websiteLink(model, ink)
    ? line(`${badge("W", accent)}&nbsp;&nbsp;${websiteLink(model, ink)}`, "margin-bottom:6px;")
    : "";
  const socialRow = socialBadgeRow(model, accent) ? line(socialBadgeRow(model, accent)) : "";

  const contactsTable = table(
    "<tr>" +
      cell(phoneRow + emailRow, "padding-right:16px;border-right:1px solid " + accent + ";") +
      cell(websiteRow + socialRow, "padding-left:16px;") +
      "</tr>",
    "margin-top:10px;"
  );

  const footer = model.branding.footerStatement.trim()
    ? table(
        `<tr><td style="padding-top:10px;border-top:1px solid #E4E8F2;text-align:right;font-size:9px;letter-spacing:1.5px;color:${accent};">${escapeHtml(model.branding.footerStatement.trim().toUpperCase())}</td></tr>`,
        "width:100%;margin-top:12px;"
      )
    : "";

  const rightArea =
    line(name, `font-size:29px;line-height:34px;font-weight:bold;color:${ink};font-family:${FONT_SANS};`) +
    line(title, "font-size:15px;color:#5A6377;margin-top:2px;") +
    contactsTable +
    footer;

  return table(
    `<tr>${cell(panelInner, `background-color:${ink};width:160px;padding:20px 18px;text-align:center;`)}${cell(rightArea, "width:480px;padding:20px;")}</tr>`,
    `width:640px;max-width:640px;font-family:${FONT_SANS};font-size:12px;line-height:18px;color:${ink};background-color:#FFFFFF;`
  );
}

// ---------------------------------------------------------------------------
// 02 / Studio — emerald editorial, serif name, divided contacts, mint edge.
// ---------------------------------------------------------------------------
function renderStudio(model: SignatureModel): string {
  const meta = THEMES.find((t) => t.id === "premium-studio-v2")!;
  const accent = resolveAccent(model, meta.defaultPalette.accent);
  const ink = meta.defaultPalette.ink;
  const name = escapeHtml(model.person.name);
  const title = escapeHtml(model.person.title);
  const company = escapeHtml(model.company.name);
  const logo = logoImgTag(model, "light", 30, 30);

  const identity =
    table(`<tr>${logo ? cell(logo, "padding-right:8px;") : ""}${cell(line(company, `font-weight:bold;color:${ink};font-size:13px;`) + (model.branding.tagline.trim() ? line(escapeHtml(model.branding.tagline.trim().toUpperCase()), "font-size:8px;letter-spacing:1.2px;color:#5C7A6A;") : ""))}</tr>`) +
    line(name, `font-size:32px;line-height:36px;font-family:${FONT_SERIF};color:${ink};margin-top:8px;`) +
    line(title, `font-size:15px;color:${accent};margin-top:2px;`) +
    (model.branding.footerStatement.trim()
      ? table(`<tr><td style="border-top:1px solid ${accent};padding-top:6px;font-size:9px;letter-spacing:1px;color:#5C7A6A;">${escapeHtml(model.branding.footerStatement.trim().toUpperCase())}</td></tr>`, "margin-top:10px;width:220px;")
      : "");

  const contacts = contactStack(model, ink, accent, 18);
  const socials = socialBadgeRow(model, accent) ? `<div style="margin-top:8px;border-top:1px solid #CFE7DA;padding-top:8px;">${socialBadgeRow(model, accent)}</div>` : "";
  const contactsCol = table(`<tr><td style="line-height:26px;">${contacts}</td></tr>`) + socials;

  // Reference (row 02/Studio) shows the stacked tagline above the
  // decorative circles, not below — matches that reading order.
  const decorative =
    (model.branding.sideStatement.trim()
      ? line(escapeHtml(model.branding.sideStatement.trim()), "font-size:9px;letter-spacing:1px;color:#5C7A6A;text-align:right;margin-bottom:10px;max-width:120px;")
      : "") +
    `<div style="text-align:right;">` +
    `<span style="display:inline-block;width:26px;height:26px;border-radius:50%;background-color:#CFE7DA;margin-right:-8px;"></span>` +
    `<span style="display:inline-block;width:40px;height:40px;border-radius:50%;background-color:#E4F3EA;"></span>` +
    `</div>`;

  return table(
    `<tr>` +
      cell(identity, "width:290px;padding:20px 16px 20px 20px;") +
      cell(contactsCol, `width:220px;padding:20px 16px;border-left:1px solid ${accent};`) +
      cell(decorative, "width:130px;padding:20px;text-align:right;vertical-align:top;") +
      `</tr>`,
    `width:640px;max-width:640px;font-family:${FONT_SANS};font-size:12px;line-height:18px;color:${ink};background-color:#FFFFFF;`
  );
}

// ---------------------------------------------------------------------------
// 03 / Prestige — ivory/bronze stationery, oversized serif name.
// ---------------------------------------------------------------------------
function renderPrestige(model: SignatureModel): string {
  const meta = THEMES.find((t) => t.id === "premium-prestige-v2")!;
  const accent = resolveAccent(model, meta.defaultPalette.accent);
  const ink = meta.defaultPalette.ink;
  const surface = meta.defaultPalette.surface;
  const name = escapeHtml(model.person.name);
  const title = escapeHtml(model.person.title);
  const company = escapeHtml(model.company.name);
  const logo = logoImgTag(model, "light", 56, 56);

  const topBand = model.branding.tagline.trim()
    ? table(
        `<tr><td style="font-size:9px;letter-spacing:2px;color:${accent};white-space:nowrap;padding-right:12px;">${escapeHtml(model.branding.tagline.trim().toUpperCase())}</td><td style="width:100%;border-top:1px solid ${accent};font-size:0;line-height:0;">&nbsp;</td></tr>`,
        "width:100%;margin-bottom:16px;"
      )
    : "";

  const mainRow = table(
    `<tr>` +
      cell(
        line(name, `font-size:36px;line-height:40px;font-family:${FONT_SERIF};color:${ink};`) +
          line(title, `font-size:15px;color:${accent};margin-top:4px;`),
        "vertical-align:middle;"
      ) +
      cell(
        (logo || `<div style="text-align:right;color:${accent};font-family:${FONT_SERIF};font-size:22px;">${companyMonogram(model)}</div>`) +
          line(company, `font-weight:bold;color:${ink};font-size:13px;text-align:right;margin-top:6px;`) +
          (model.branding.footerStatement.trim()
            ? line(escapeHtml(model.branding.footerStatement.trim().toUpperCase()), `font-size:8px;letter-spacing:1.5px;color:${accent};text-align:right;margin-top:4px;`)
            : ""),
        "width:200px;text-align:right;vertical-align:top;"
      ) +
      `</tr>`,
    "width:100%;margin-bottom:18px;"
  );

  const parts: string[] = [];
  if (model.person.phone.trim()) {
    const tel = model.person.phone.replace(/[^+0-9]/g, "");
    parts.push((tel ? link(`tel:${tel}`, escapeHtml(model.person.phone), ink) : escapeHtml(model.person.phone)));
  }
  if (model.person.email.trim() && /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(model.person.email)) {
    parts.push(link(`mailto:${encodeURIComponent(model.person.email)}`, escapeHtml(model.person.email), ink));
  }
  const site = websiteLink(model, ink);
  if (site) parts.push(site);
  const contactBand = parts.length
    ? `<div style="border-top:1px solid ${accent};padding-top:10px;font-size:12px;">${parts.join(`&nbsp;&nbsp;<span style="color:${accent};">|</span>&nbsp;&nbsp;`)}</div>`
    : "";

  // Per the approved reference (row 03/Prestige), this design has no
  // separate social-icon row at all — unlike Executive/Studio/Connect,
  // socials simply aren't a feature of this theme's layout.
  return table(
    `<tr><td style="padding:22px;">${topBand}${mainRow}${contactBand}</td></tr>`,
    `width:640px;max-width:640px;background-color:${surface};font-family:${FONT_SANS};font-size:12px;line-height:18px;color:${ink};`
  );
}

// ---------------------------------------------------------------------------
// 04 / Connect — round brand badge, tinted contact card, social footer.
// ---------------------------------------------------------------------------
function renderConnect(model: SignatureModel): string {
  const meta = THEMES.find((t) => t.id === "premium-connect-v2")!;
  const accent = resolveAccent(model, meta.defaultPalette.accent);
  const ink = meta.defaultPalette.ink;
  const name = escapeHtml(model.person.name);
  const title = escapeHtml(model.person.title);
  const company = escapeHtml(model.company.name);
  const badgeUrl = resolveLogo(model, "dark");
  const badgeInner = badgeUrl
    ? `<img src="${escapeAttr(badgeUrl)}" alt="${escapeAttr(model.logos.alt || model.company.name || "Company logo")}" width="72" height="72" style="display:block;border:0;width:72px;height:72px;border-radius:50%;object-fit:cover;" />`
    : `<span style="display:block;width:72px;height:72px;line-height:72px;border-radius:50%;background-color:${ink};color:#FFFFFF;text-align:center;font-family:${FONT_SANS};font-size:28px;font-weight:bold;">${companyMonogram(model)}</span>`;

  const identity =
    line(name, `font-size:28px;line-height:32px;font-weight:bold;color:${ink};`) +
    line(title, `font-size:15px;color:${accent};margin-top:2px;`) +
    line(company, "font-size:13px;color:#5A6377;margin-top:2px;") +
    (model.branding.tagline.trim() ? line(escapeHtml(model.branding.tagline.trim().toUpperCase()), "font-size:9px;letter-spacing:1.5px;color:#8891A8;margin-top:6px;") : "");

  const contactRows: string[] = [];
  if (model.person.phone.trim()) {
    const tel = model.person.phone.replace(/[^+0-9]/g, "");
    contactRows.push(`${badge("T", ink)}&nbsp;&nbsp;${tel ? link(`tel:${tel}`, escapeHtml(model.person.phone), ink) : escapeHtml(model.person.phone)}`);
  }
  if (model.person.email.trim() && /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(model.person.email)) {
    contactRows.push(`${badge("E", ink)}&nbsp;&nbsp;${link(`mailto:${encodeURIComponent(model.person.email)}`, escapeHtml(model.person.email), ink)}`);
  }
  const site = websiteLink(model, ink);
  if (site) contactRows.push(`${badge("W", ink)}&nbsp;&nbsp;${site}`);

  const contactCard = contactRows.length
    ? table(
        contactRows.map((row, i) => `<tr>${cell(row, `padding:6px 0;${i > 0 ? "border-top:1px solid #D7E1FA;" : ""}`)}</tr>`).join(""),
        "width:100%;background-color:#EDF2FF;border-radius:10px;padding:12px 14px;"
      )
    : "";

  const socials = socialBadgeRow(model, ink);
  const footer = table(
    `<tr><td style="border-top:1px solid ${accent};padding-top:10px;">${websiteLink(model, ink)}</td><td style="border-top:1px solid ${accent};padding-top:10px;text-align:right;">${socials}${
      model.branding.footerStatement.trim() ? `&nbsp;&nbsp;<span style="font-size:9px;letter-spacing:1px;color:#5A6377;">${escapeHtml(model.branding.footerStatement.trim())}</span>` : ""
    }</td></tr>`,
    "width:100%;margin-top:16px;"
  );

  return table(
    `<tr>` +
      cell(badgeInner, "width:120px;padding:20px;vertical-align:top;") +
      cell(identity, `width:300px;padding:20px 16px;border-left:1px solid #E4E8F2;`) +
      cell(contactCard, "width:220px;padding:20px 20px 20px 0;vertical-align:top;") +
      `</tr>` +
      `<tr><td colspan="3" style="padding:0 20px 20px;">${footer}</td></tr>`,
    `width:640px;max-width:640px;font-family:${FONT_SANS};font-size:12px;line-height:18px;color:${ink};background-color:#FFFFFF;`
  );
}

// ---------------------------------------------------------------------------
// 05 / Essential — cobalt rail, concise identity, contact strip.
// ---------------------------------------------------------------------------
function renderEssential(model: SignatureModel): string {
  const meta = THEMES.find((t) => t.id === "premium-essential-v2")!;
  const accent = resolveAccent(model, meta.defaultPalette.accent);
  const ink = meta.defaultPalette.ink;
  const name = escapeHtml(model.person.name);
  const title = escapeHtml(model.person.title);
  const company = escapeHtml(model.company.name);
  const logo = logoImgTag(model, "light", 56, 56);

  const rail =
    (model.branding.sideStatement.trim()
      ? line(escapeHtml(model.branding.sideStatement.trim().toUpperCase()), "color:#FFFFFF;font-size:9px;letter-spacing:1.5px;line-height:15px;")
      : "") + `<div style="width:24px;border-top:1px solid rgba(255,255,255,0.6);margin-top:8px;"></div>`;

  const parts: string[] = [];
  if (model.person.phone.trim()) {
    const tel = model.person.phone.replace(/[^+0-9]/g, "");
    parts.push(tel ? link(`tel:${tel}`, escapeHtml(model.person.phone), ink) : escapeHtml(model.person.phone));
  }
  if (model.person.email.trim() && /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(model.person.email)) {
    parts.push(link(`mailto:${encodeURIComponent(model.person.email)}`, escapeHtml(model.person.email), ink));
  }
  const site = websiteLink(model, ink);
  if (site) parts.push(site);
  const contactStrip = parts.length ? parts.join(`&nbsp;&nbsp;<span style="color:${accent};">|</span>&nbsp;&nbsp;`) : "";

  const main =
    line(name, `font-size:26px;line-height:30px;font-weight:bold;color:${ink};`) +
    line(title, `font-size:15px;color:${accent};margin-top:2px;`) +
    line(company, "font-size:13px;color:#5A6377;margin-top:2px;") +
    // Per the approved reference (row 05/Essential), this design has no
    // separate social-icon row either — same finding as Prestige.
    (contactStrip ? `<div style="border-top:1px solid #E4E8F2;margin-top:10px;padding-top:8px;font-size:12px;">${contactStrip}</div>` : "");

  const lockup =
    (logo || `<div style="color:${accent};font-family:${FONT_SANS};font-size:22px;font-weight:bold;text-align:right;">${companyMonogram(model)}</div>`) +
    line(company, "font-size:11px;color:#5A6377;text-align:right;margin-top:6px;") +
    (model.branding.footerStatement.trim()
      ? table(`<tr><td style="text-align:right;font-size:8px;letter-spacing:1.2px;color:${accent};border-top:1px solid #E4E8F2;padding-top:4px;">${escapeHtml(model.branding.footerStatement.trim().toUpperCase())}</td></tr>`, "margin-top:6px;width:100%;")
      : "");

  return table(
    `<tr>` +
      cell(rail, `width:80px;background-color:${accent};padding:20px 12px;`) +
      cell(main, "width:440px;padding:22px;") +
      cell(lockup, "width:120px;padding:22px 22px 22px 0;text-align:right;vertical-align:top;") +
      `</tr>`,
    `width:640px;max-width:640px;font-family:${FONT_SANS};font-size:12px;line-height:18px;color:${ink};background-color:#FFFFFF;`
  );
}

// ---------------------------------------------------------------------------
// Basic — the plain, no-theme option: logo on top, name, title, company,
// and a plain phone/email line. No accent color, no social badges, no
// disclaimer row, no decorative elements — deliberately minimal per the
// "some people just want to show it normally" request. Uses fixed neutral
// ink/gray tones, never the model's brand accent.
// ---------------------------------------------------------------------------
function renderBasic(model: SignatureModel): string {
  const name = escapeHtml(model.person.name);
  const title = escapeHtml(model.person.title);
  const company = escapeHtml(model.company.name);
  const logo = logoImgTag(model, "light", 56, 56);
  const ink = "#1A1A1A";
  const muted = "#4A4A4A";
  const link_ = "#1A1A1A";

  const parts: string[] = [];
  if (model.person.phone.trim()) {
    const tel = model.person.phone.replace(/[^+0-9]/g, "");
    parts.push(tel ? link(`tel:${tel}`, escapeHtml(model.person.phone), link_) : escapeHtml(model.person.phone));
  }
  if (model.person.email.trim() && /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(model.person.email)) {
    parts.push(link(`mailto:${encodeURIComponent(model.person.email)}`, escapeHtml(model.person.email), link_));
  }
  const contactLine = parts.length ? parts.join("&nbsp;&nbsp;|&nbsp;&nbsp;") : "";

  const content =
    (logo ? line(logo, "margin-bottom:10px;") : "") +
    line(name, `font-size:16px;line-height:20px;font-weight:bold;color:${ink};font-family:${FONT_SANS};`) +
    (title ? line(title, `font-size:13px;color:${muted};margin-top:2px;font-family:${FONT_SANS};`) : "") +
    (company ? line(company, `font-size:13px;color:${muted};margin-top:2px;font-family:${FONT_SANS};`) : "") +
    (contactLine ? line(contactLine, `font-size:12px;color:${muted};margin-top:8px;font-family:${FONT_SANS};`) : "");

  return table(
    `<tr><td style="padding:14px 0;">${content}</td></tr>`,
    `width:420px;max-width:420px;font-family:${FONT_SANS};font-size:13px;line-height:18px;color:${ink};background-color:#FFFFFF;`
  );
}

export function renderTheme(model: SignatureModel): string {
  switch (model.themeId) {
    case "basic":
      return renderBasic(model);
    case "premium-executive-v2":
      return renderExecutive(model);
    case "premium-studio-v2":
      return renderStudio(model);
    case "premium-prestige-v2":
      return renderPrestige(model);
    case "premium-connect-v2":
      return renderConnect(model);
    case "premium-essential-v2":
      return renderEssential(model);
    default:
      throw new Error(`Unknown theme: ${model.themeId}`);
  }
}
